import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ReviewForm } from "./ReviewForm";
import { useTranslation } from "react-i18next";
import { useLocalized } from "@/hooks/useLocalized";
import { LocalizedContent } from "@/types";
import { useState } from "react";

interface Review {
  id: string;
  rating: number;
  comment: LocalizedContent;
  reviewer_name: string;
  created_at: string;
}

interface TestimonialsSectionProps {
  reviews: Review[];
  averageRating?: number;
  totalReviews?: number;
}

const INITIAL_COUNT = 6;
const LOAD_MORE_Count = 6;

export function TestimonialsSection({
  reviews,
  averageRating = 0,
  totalReviews = 0
}: TestimonialsSectionProps) {
  const { t } = useTranslation();
  const { getLocalized } = useLocalized();
  const [visibleCount, setVisibleCount] = useState(INITIAL_COUNT);

  const displayReviews = reviews.slice(0, visibleCount);
  const hasMore = visibleCount < reviews.length;

  const handleLoadMore = () => {
    setVisibleCount(prev => prev + LOAD_MORE_Count);
  };

  return (
    <section className="py-12 sm:py-16 md:py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 sm:mb-6">
            {t('testimonials.title', 'Client Testimonials')}
          </h2>

          {totalReviews > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`h-5 w-5 sm:h-6 sm:w-6 ${i < Math.floor(averageRating) ? 'text-[hsl(var(--gold))] fill-current' : 'text-muted-foreground/30'}`}
                  />
                ))}
              </div>
              <span className="text-xl sm:text-2xl font-semibold">{averageRating}</span>
              <span className="text-sm sm:text-base text-muted-foreground">({totalReviews} {t('testimonials.reviews', 'reviews')})</span>
            </div>
          )}

          <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto px-4">
            {t('testimonials.subtitle', 'What my clients say about their photography experience')}
          </p>
        </div>

        {reviews.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {displayReviews.map((review) => (
                <Card key={review.id} className="relative overflow-hidden hover:shadow-[var(--shadow-elegant)] transition-all duration-300">
                  <CardContent className="p-6">
                    <div className="absolute top-4 right-4 opacity-10">
                      <Quote className="h-12 w-12" />
                    </div>

                    <div className="flex mb-4">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${i < review.rating ? 'text-[hsl(var(--gold))] fill-current' : 'text-muted-foreground/30'}`}
                        />
                      ))}
                    </div>

                    <p className="text-muted-foreground mb-4 italic">
                      "{getLocalized(review.comment)}"
                    </p>

                    <div className="border-t pt-4">
                      <p className="font-semibold">{review.reviewer_name}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(review.created_at).toLocaleDateString(t('common.dateLocale', 'en-US'), {
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="flex justify-center mt-8">
                <Button
                  variant="outline"
                  onClick={handleLoadMore}
                  className="rounded-full px-8 gap-2"
                >
                  {t('testimonials.loadMore', 'Load More')}
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <div className="max-w-md mx-auto">
              <div className="mb-6">
                <Quote className="h-16 w-16 mx-auto text-muted-foreground/30" />
              </div>
              <h3 className="text-2xl font-semibold mb-4">{t('testimonials.comingSoonTitle', 'Coming Soon')}</h3>
              <p className="text-muted-foreground">
                {t('testimonials.comingSoonText', 'Client testimonials will appear here once sessions are completed and reviewed.')}
              </p>
            </div>
          </div>
        )}

        {/* Review Form */}
        <div className="mt-12 sm:mt-16">
          <ReviewForm />
        </div>
      </div>
    </section>
  );
}